import { useEffect, useState } from 'react'

interface Controller {
  id: number
  identifier: string
  name: string | null
  is_active: boolean
  last_seen_at: string | null
  points_count: number
}

interface ControllersModalProps {
  isOpen: boolean
  token: string | null
  onClose: () => void
}

export default function ControllersModal({ isOpen, token, onClose }: ControllersModalProps) {
  const [controllers, setControllers] = useState<Controller[]>([])
  const [loading, setLoading] = useState(false)
  const [identifier, setIdentifier] = useState('')
  const [name, setName] = useState('')
  const [creating, setCreating] = useState(false)
  const [error, setError] = useState('')
  // Token pokazywany JEDEN raz po utworzeniu/rotacji
  const [oneTimeToken, setOneTimeToken] = useState<{ id: number; identifier: string; token: string } | null>(null)

  const authHeaders = { 'Authorization': `Bearer ${token}` }

  const load = async () => {
    setLoading(true)
    try {
      const r = await fetch('/api/telemetry/controllers', { headers: authHeaders })
      setControllers(r.ok ? await r.json() : [])
    } catch {
      setControllers([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (isOpen) { setError(''); setOneTimeToken(null); load() }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  if (!isOpen) return null

  const handleCreate = async () => {
    if (!identifier.trim()) { setError('Podaj identyfikator kontrolera (serial/MAC).'); return }
    setCreating(true); setError('')
    try {
      const r = await fetch('/api/telemetry/controllers', {
        method: 'POST',
        headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify({ identifier: identifier.trim(), name: name.trim() || null }),
      })
      const data = await r.json()
      if (!r.ok) { setError(data.detail || 'Nie udało się utworzyć kontrolera'); return }
      setOneTimeToken({ id: data.id, identifier: data.identifier, token: data.token })
      setIdentifier(''); setName('')
      load()
    } catch {
      setError('Wystąpił problem z połączeniem')
    } finally {
      setCreating(false)
    }
  }

  const handleRotate = async (c: Controller) => {
    try {
      const r = await fetch(`/api/telemetry/controllers/${c.id}/rotate-token`, { method: 'POST', headers: authHeaders })
      const data = await r.json()
      if (r.ok) setOneTimeToken({ id: c.id, identifier: c.identifier, token: data.token })
    } catch { /* ignore */ }
  }

  const handleDelete = async (c: Controller) => {
    try {
      const r = await fetch(`/api/telemetry/controllers/${c.id}`, { method: 'DELETE', headers: authHeaders })
      if (r.ok) load()
    } catch { /* ignore */ }
  }

  const copy = (t: string) => { try { navigator.clipboard?.writeText(t) } catch { /* ignore */ } }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-stretch justify-center z-[9999] sm:items-center sm:p-4" onClick={onClose}>
      <div className="bg-white sm:rounded-xl shadow-2xl w-full sm:max-w-lg flex flex-col max-h-full sm:max-h-[90vh] overflow-hidden" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 sm:px-6 py-4 border-b border-gray-200 shrink-0">
          <h2 className="text-lg font-semibold text-gray-900">📟 Kontrolery telemetryczne</h2>
          <button onClick={onClose} className="p-1 -mr-1 text-gray-400 hover:text-gray-600 text-xl font-bold">✕</button>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto px-5 sm:px-6 py-4 space-y-4">
          {/* Token pokazany raz */}
          {oneTimeToken && (
            <div className="bg-amber-50 border border-amber-300 rounded-xl p-3">
              <p className="text-sm font-semibold text-amber-800 mb-1">Token dla „{oneTimeToken.identifier}" — zapisz teraz!</p>
              <p className="text-xs text-amber-700 mb-2">Nie będzie pokazany ponownie. Wgraj go do kontrolera.</p>
              <div className="flex items-center gap-2">
                <code className="flex-1 min-w-0 break-all bg-white border border-amber-200 rounded-lg px-2 py-1.5 text-xs text-gray-800">{oneTimeToken.token}</code>
                <button onClick={() => copy(oneTimeToken.token)} className="shrink-0 min-h-[36px] px-3 rounded-lg text-sm bg-amber-600 text-white hover:bg-amber-700">Kopiuj</button>
              </div>
            </div>
          )}

          {/* Formularz nowego kontrolera */}
          <div className="rounded-xl border border-gray-200 p-3">
            <p className="text-sm font-medium text-gray-800 mb-2">Zarejestruj kontroler</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <input
                value={identifier}
                onChange={e => setIdentifier(e.target.value)}
                placeholder="Identyfikator (serial/MAC) *"
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
              />
              <input
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder="Nazwa (opcjonalnie)"
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
              />
            </div>
            {error && <p className="text-xs text-red-600 mt-2">{error}</p>}
            <button
              onClick={handleCreate}
              disabled={creating}
              className="mt-2 min-h-[40px] px-4 rounded-lg text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
            >
              {creating ? 'Tworzenie…' : '+ Utwórz i wygeneruj token'}
            </button>
          </div>

          {/* Lista kontrolerów */}
          <div>
            <p className="text-sm font-medium text-gray-800 mb-2">Zarejestrowane kontrolery</p>
            {loading ? (
              <p className="text-sm text-gray-500">Ładowanie…</p>
            ) : controllers.length === 0 ? (
              <p className="text-sm text-gray-500">Brak kontrolerów.</p>
            ) : (
              <ul className="divide-y divide-gray-100 border border-gray-100 rounded-xl overflow-hidden">
                {controllers.map(c => (
                  <li key={c.id} className="p-3 flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{c.identifier}</p>
                      <p className="text-xs text-gray-500">
                        {c.name ? `${c.name} • ` : ''}{c.points_count} punkt(ów)
                        {c.last_seen_at ? ` • ostatnio: ${new Date(c.last_seen_at).toLocaleString('pl-PL')}` : ' • brak łączności'}
                      </p>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      <button onClick={() => handleRotate(c)} className="min-h-[32px] px-2 rounded-lg text-xs text-blue-700 bg-blue-50 hover:bg-blue-100" title="Nowy token">🔑</button>
                      <button onClick={() => handleDelete(c)} className="min-h-[32px] px-2 rounded-lg text-xs text-red-700 bg-red-50 hover:bg-red-100" title="Usuń">🗑</button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        <div className="flex justify-end px-5 sm:px-6 py-4 border-t border-gray-200 shrink-0">
          <button onClick={onClose} className="min-h-[44px] px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg">Zamknij</button>
        </div>
      </div>
    </div>
  )
}
